import React, { useState, useContext, useRef } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';

import SidePanel from '../components/Common/SidePanel';
import MobileMenuToggle from '../components/Common/MobileMenuToggle';
import { Upload, FileText, Loader, AlertCircle, CheckCircle } from 'lucide-react';
import { barcodeService } from '../services/api';

const Import = () => {
  const { user } = useContext(AuthContext);
  const { isCollapsed } = useSidebar();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError('');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      if (!droppedFile.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        setFile(null);
        return;
      }
      setFile(droppedFile);
      setError('');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      setError('Please select a file');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess(false);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await barcodeService.bulkImport(formData);
      setSuccess(true);
      setResult({
        total: response.data?.data?.total || 0,
        imported: response.data?.data?.imported || 0,
        failed: response.data?.data?.failed || 0
      });
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to import file');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex h-screen bg-slate-50">
      <MobileMenuToggle onClick={() => setIsSidebarOpen(true)} isSidebarOpen={isSidebarOpen} />
      <SidePanel isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className="flex-1 flex flex-col overflow-hidden transition-all duration-300">
        <main className="flex-1 overflow-y-auto pt-28 md:pt-0">
          <div className="p-6 md:p-8  mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-slate-900 mb-2">Bulk Import Barcodes</h1>
              <p className="text-slate-600">Import barcodes from CSV file</p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-red-800">{error}</p>
              </div>
            )}

            {success && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex gap-3 mb-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-green-800 font-medium">Import completed successfully!</p>
                </div>
                {result && (
                  <div className="text-sm text-green-700 grid grid-cols-3 gap-4">
                    <div>
                      <p className="font-semibold text-lg">{result.total}</p>
                      <p>Total Processed</p>
                    </div>
                    <div>
                      <p className="font-semibold text-lg text-green-600">{result.imported}</p>
                      <p>Imported</p>
                    </div>
                    <div>
                      <p className="font-semibold text-lg text-red-600">{result.failed}</p>
                      <p>Failed</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="bg-white rounded-lg shadow p-6">
              <form onSubmit={handleUpload} className="space-y-6">
                <div
                  onDrop={handleDrop}
                  onDragOver={(e) => e.preventDefault()}
                  className="border-2 border-dashed border-slate-300 rounded-lg p-12 text-center hover:border-blue-400 hover:bg-blue-50 transition cursor-pointer"
                >
                  <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Upload CSV File</h3>
                  <p className="text-slate-600 mb-4">Drag and drop your CSV file here or click to browse</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
                  >
                    Choose File
                  </button>
                  {file && (
                    <p className="mt-4 text-sm text-green-700">
                      Selected: <span className="font-semibold">{file.name}</span>
                    </p>
                  )}
                </div>

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    CSV Format Requirements
                  </h4>
                  <ul className="text-sm text-blue-800 space-y-2">
                    <li>• <strong>Row 1:</strong> Header row (will be skipped) - e.g., "barcodeNumber"</li>
                    <li>• <strong>Column 1:</strong> Barcode Number (required) - e.g., RO - 35662</li>
                    <li>• <strong>Column 2:</strong> QR Code Image/Data (optional)</li>
                    <li>• File must be in .csv format</li>
                    <li>• Maximum file size: 10 MB</li>
                  </ul>
                </div>

                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h4 className="font-semibold text-yellow-900 mb-2">Sample CSV Format</h4>
                  <pre className="text-xs text-yellow-800 overflow-x-auto bg-white p-3 rounded border border-yellow-200">
{`barcodeNumber,qrCode
RO - 35662,
RO - 35663,data:image/png;base64,...
RO - 35664,`}
                  </pre>
                </div>

                <button
                  type="submit"
                  disabled={!file || uploading}
                  className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition disabled:bg-slate-400"
                >
                  {uploading ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      Import Barcodes
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Import;
